import { useEffect, useState } from 'react'
import { formatCode } from '../../shared/code'
import { CLIENT_ITERATIONS } from '../../shared/passphrase'
import type { InviteInfo } from '../../shared/types'
import * as api from '../api'
import { Button, Card, Row, SectionTitle, Sheet, Spinner, Toggle } from '../components/ui'
import { LOCALES, localeName, useI18n } from '../i18n'
import { useReady, useStore } from '../store'

const TIMEZONES = [
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Taipei',
  'Asia/Tokyo',
  'Asia/Singapore',
  'America/Los_Angeles',
  'America/Chicago',
  'America/New_York',
  'Europe/London',
  'Europe/Berlin',
  'Australia/Sydney',
  'UTC',
]

export function Settings() {
  const { t } = useI18n()
  const { snapshot, updateSettings, signOut, reload } = useReady()
  const s = snapshot.settings
  const [pwOpen, setPwOpen] = useState(false)
  const [emailOpen, setEmailOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  const patch = async (p: Parameters<typeof updateSettings>[0]) => {
    setBusy(true)
    try {
      await updateSettings(p)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="px-4 pb-6 space-y-5">
      <header className="safe-top pb-1 px-1">
        <h1 className="text-2xl font-semibold">{t.settings.title}</h1>
        <p className="text-sm text-muted mt-1">
          {snapshot.user.displayName} · {snapshot.user.loginName}
        </p>
      </header>

      <div>
        <SectionTitle>{t.settings.household}</SectionTitle>
        <Card className="py-1">
          <Row label={t.settings.householdName}>
            <input
              defaultValue={s.name}
              onBlur={(e) => {
                const v = e.target.value.trim()
                if (v && v !== s.name) void patch({ name: v })
              }}
              className="bg-transparent text-right outline-none w-32 text-[15px]"
            />
          </Row>
          <Row label={t.settings.timezone} hint={t.settings.timezoneHint}>
            <select
              value={s.timezone}
              onChange={(e) => void patch({ timezone: e.target.value })}
              className="bg-bg-soft rounded-xl px-3 py-2 text-sm outline-none max-w-[11rem]"
            >
              {(TIMEZONES.includes(s.timezone) ? TIMEZONES : [s.timezone, ...TIMEZONES]).map(
                (tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ),
              )}
            </select>
          </Row>
          <Row label={t.settings.language} hint={t.settings.languageHint}>
            <LanguagePicker />
          </Row>
        </Card>
      </div>

      <PlanSection />

      <div>
        <SectionTitle>{t.settings.defaults}</SectionTitle>
        <Card className="py-1">
          <Row label={t.settings.defaultCycle}>
            <NumberStepper
              value={s.defaultCycleLen}
              min={20}
              max={45}
              disabled={busy}
              onChange={(v) => void patch({ defaultCycleLen: v })}
            />
          </Row>
          <Row label={t.settings.defaultPeriod} hint={t.settings.defaultsHint}>
            <NumberStepper
              value={s.defaultPeriodLen}
              min={2}
              max={12}
              disabled={busy}
              onChange={(v) => void patch({ defaultPeriodLen: v })}
            />
          </Row>
        </Card>
      </div>

      <div>
        <SectionTitle>{t.settings.advanced}</SectionTitle>
        <Card className="py-1">
          <Row label={t.settings.trackBbt} hint={t.settings.trackBbtHint}>
            <Toggle
              checked={s.trackBbt}
              label={t.settings.trackBbt}
              onChange={(v) => void patch({ trackBbt: v })}
            />
          </Row>
          <Row label={t.settings.trackLh} hint={t.settings.trackLhHint}>
            <Toggle
              checked={s.trackLh}
              label={t.settings.trackLh}
              onChange={(v) => void patch({ trackLh: v })}
            />
          </Row>
        </Card>
      </div>

      <div>
        <SectionTitle>{t.settings.data}</SectionTitle>
        <Card className="py-1">
          <Row label={t.settings.exportData} hint={t.settings.exportHint}>
            <Button variant="soft" className="text-sm" onClick={() => void api.exportData()}>
              {t.settings.exportAction}
            </Button>
          </Row>
        </Card>
      </div>

      <div>
        <SectionTitle>{t.settings.account}</SectionTitle>
        <Card className="py-1">
          <Row
            label={t.settings.email}
            hint={snapshot.user.email ?? t.settings.emailWhy}
          >
            <Button variant="soft" className="text-sm" onClick={() => setEmailOpen(true)}>
              {snapshot.user.email ? t.settings.emailEdit : t.settings.emailAdd}
            </Button>
          </Row>
          <Row label={t.settings.changePassword}>
            <Button variant="soft" className="text-sm" onClick={() => setPwOpen(true)}>
              {t.settings.changePasswordAction}
            </Button>
          </Row>
          <Row label={t.settings.logout}>
            <Button variant="danger" className="text-sm" onClick={signOut}>
              {t.settings.logoutAction}
            </Button>
          </Row>
        </Card>
      </div>

      <DangerSection />

      {/* 这段必须显眼且不可关闭 —— 把推算结果当避孕手段是会出事的 */}
      <Card className="bg-period-soft border-transparent">
        <p className="text-sm font-medium text-period mb-2">{t.settings.disclaimerTitle}</p>
        <p className="text-[13px] leading-relaxed text-ink/80">{t.settings.disclaimer}</p>
      </Card>

      <p className="text-center text-xs text-muted pb-2">
        {/*
          原文是「数据存在你们自己的 Cloudflare 账号里」。自部署时那是对的，
          但现在是我在托管、别人来注册用 —— 对这些用户来说数据在我的账号里，
          再这么写就是一句不实的隐私承诺了。
        */}
        {t.appName} · {t.settings.footer}
      </p>

      <PasswordSheet
        open={pwOpen}
        onClose={() => setPwOpen(false)}
        loginName={snapshot.user.loginName}
      />

      <EmailSheet
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        loginName={snapshot.user.loginName}
        current={snapshot.user.email}
        onSaved={() => void reload()}
      />
    </div>
  )
}

/**
 * 语言。切换是立刻生效的（存在这台设备上），随后 App 会把它同步到账号上，
 * 好让找回口令的邮件也说同一门语言。
 */
function LanguagePicker() {
  const { locale, setLocale } = useI18n()
  return (
    <select
      value={locale}
      onChange={(e) => setLocale(e.target.value as typeof locale)}
      className="bg-bg-soft rounded-xl px-3 py-2 text-sm outline-none max-w-[11rem]"
    >
      {LOCALES.map((l) => (
        <option key={l} value={l}>
          {localeName(l)}
        </option>
      ))}
    </select>
  )
}

// ── 席位 / 邀请 / 兑换 ──────────────────────────────────────────────────

function PlanSection() {
  const { t, formatDate } = useI18n()
  const { snapshot, reload } = useReady()
  const { plan, members } = snapshot
  const [invite, setInvite] = useState<InviteInfo | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [redeemOpen, setRedeemOpen] = useState(false)

  // 有效邀请码不在快照里（它不是「数据」，是个短命的一次性凭证），单独取一次
  useEffect(() => {
    let cancelled = false
    api
      .fetchInvite()
      .then((r) => {
        if (!cancelled) setInvite(r.invite)
      })
      .catch(() => {
        /* 取不到就当没有，用户点生成时还会再试一次 */
      })
    return () => {
      cancelled = true
    }
  }, [plan.memberCount])

  const create = async () => {
    setBusy(true)
    setError(null)
    try {
      setInvite((await api.createInvite()).invite)
    } catch (e) {
      setError(e instanceof Error ? e.message : t.common.error)
    } finally {
      setBusy(false)
    }
  }

  const revoke = async () => {
    setBusy(true)
    try {
      await api.revokeInvite()
      setInvite(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <SectionTitle>{t.plan.title}</SectionTitle>
      <Card className="py-1">
        <Row
          label={plan.plan === 'couple' ? t.plan.couple : t.plan.solo}
          hint={t.plan.seatsUsed(plan.memberCount, plan.seats)}
        />

        {members.map((m) => (
          <Row
            key={m.id}
            label={m.displayName}
            hint={m.role === 'tracker' ? t.plan.roleTracker : t.plan.rolePartner}
          >
            {m.id === snapshot.user.id && (
              <span className="text-xs text-muted">{t.plan.you}</span>
            )}
          </Row>
        ))}

        {/* 席位没满 → 可以邀请；席位满且只有一个人 → 卖点在这里 */}
        {plan.canInvite ? (
          invite ? (
            <div className="py-3 space-y-3">
              <div className="text-[15px]">{t.plan.inviteActive}</div>
              <InviteCode code={invite.code} />
              <p className="text-xs text-muted leading-relaxed">{t.plan.inviteHint}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted">
                  {t.plan.inviteExpires(formatDate(invite.expiresAt.slice(0, 10)))}
                </span>
                <Button
                  variant="ghost"
                  className="text-sm px-2"
                  disabled={busy}
                  onClick={() => void revoke()}
                >
                  {t.plan.inviteRevoke}
                </Button>
              </div>
            </div>
          ) : (
            <Row label={t.plan.invitePartner} hint={t.plan.inviteHint}>
              <Button
                variant="soft"
                className="text-sm"
                disabled={busy}
                onClick={() => void create()}
              >
                {busy ? <Spinner /> : t.plan.inviteCreate}
              </Button>
            </Row>
          )
        ) : plan.memberCount < 2 ? (
          <Row label={t.plan.locked} hint={t.plan.lockedHint}>
            <Button variant="soft" className="text-sm" onClick={() => setRedeemOpen(true)}>
              {t.plan.redeemSubmit}
            </Button>
          </Row>
        ) : (
          <Row label={t.plan.seatsFull} hint={t.plan.sameDataNote} />
        )}

        {error && <p className="text-sm text-period py-2">{error}</p>}
      </Card>

      <RedeemSheet
        open={redeemOpen}
        onClose={() => setRedeemOpen(false)}
        onDone={() => void reload()}
      />
    </div>
  )
}

/** 邀请码 + 一键分享。iOS 上走系统分享面板，其余情况退回复制。 */
function InviteCode({ code }: { code: string }) {
  const { t } = useI18n()
  const [copied, setCopied] = useState(false)
  const pretty = formatCode(code)
  const url = `${window.location.origin}/?invite=${pretty}`

  const share = async () => {
    const text = `${url}`
    try {
      if (navigator.share) {
        await navigator.share({ title: t.appName, text: t.plan.invitePartner, url: text })
        return
      }
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* 用户取消分享，或者浏览器不给剪贴板权限；码本身就在屏幕上，能手抄 */
    }
  }

  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 rounded-2xl bg-bg-soft px-4 py-3 text-[17px] tracking-[0.15em] tabular-nums select-all">
        {pretty}
      </code>
      <Button variant="soft" className="text-sm shrink-0" onClick={() => void share()}>
        {copied ? t.plan.inviteCopied : t.plan.inviteCopy}
      </Button>
    </div>
  )
}

function RedeemSheet({
  open,
  onClose,
  onDone,
}: {
  open: boolean
  onClose: () => void
  onDone: () => void
}) {
  const { t } = useI18n()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  const submit = async () => {
    setBusy(true)
    setMsg(null)
    try {
      await api.redeem(code)
      setMsg({ kind: 'ok', text: t.plan.redeemDone })
      setCode('')
      onDone()
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : t.common.error })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t.plan.redeemTitle}
      footer={
        <Button className="w-full" onClick={() => void submit()} disabled={busy || !code.trim()}>
          {busy ? (
            <span className="inline-flex items-center gap-2">
              <Spinner /> {t.plan.redeemWorking}
            </span>
          ) : (
            t.plan.redeemSubmit
          )}
        </Button>
      }
    >
      <div className="space-y-3 pb-2">
        <input
          type="text"
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          placeholder={t.plan.redeemPlaceholder}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="w-full rounded-2xl bg-bg-soft px-4 py-3 text-[16px] tracking-[0.15em] uppercase outline-none border border-transparent focus:border-line"
        />
        {msg && (
          <p className={`text-sm ${msg.kind === 'ok' ? 'text-fertile' : 'text-period'}`}>
            {msg.text}
          </p>
        )}
        {/* 兑换成功之后这句就过时了（席位已经不是 1 了），别再摆在那儿误导人 */}
        {msg?.kind !== 'ok' && (
          <p className="text-xs text-muted leading-relaxed pt-1">{t.plan.lockedHint}</p>
        )}
      </div>
    </Sheet>
  )
}

// ── 改邮箱 ──────────────────────────────────────────────────────────────

function EmailSheet({
  open,
  onClose,
  loginName,
  current,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  loginName: string
  current: string | null
  onSaved: () => void
}) {
  const { t } = useI18n()
  const [email, setEmail] = useState(current ?? '')
  const [passphrase, setPassphrase] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  // 发信没开通的话，填了也白填，得先说清楚
  const [recovery, setRecovery] = useState(true)

  useEffect(() => {
    if (!open) return
    setEmail(current ?? '')
    setPassphrase('')
    setMsg(null)
    api
      .fetchConfig()
      .then((cfg) => setRecovery(cfg.emailRecovery))
      .catch(() => setRecovery(true))
  }, [open, current])

  const clearing = email.trim() === ''

  const submit = async () => {
    setBusy(true)
    setMsg(null)
    try {
      const res = await api.changeEmail(loginName, email.trim(), passphrase)
      setMsg({
        kind: 'ok',
        text: res.email ? t.settings.emailSaved : t.settings.emailCleared,
      })
      setPassphrase('')
      onSaved()
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : t.common.error })
    } finally {
      setBusy(false)
    }
  }

  const input =
    'w-full rounded-2xl bg-bg-soft px-4 py-3 text-[15px] outline-none border border-transparent focus:border-line'

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t.settings.emailSheetTitle}
      footer={
        <Button className="w-full" onClick={() => void submit()} disabled={busy || !passphrase}>
          {busy ? (
            <span className="inline-flex items-center gap-2">
              <Spinner /> {t.settings.working}
            </span>
          ) : (
            t.settings.save
          )}
        </Button>
      }
    >
      <div className="space-y-3 pb-2">
        <input
          type="email"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="email"
          placeholder={t.settings.emailPlaceholder}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={input}
        />
        <input
          type="password"
          autoComplete="current-password"
          placeholder={t.settings.currentPassword}
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          className={input}
        />
        {msg && (
          <p className={`text-sm ${msg.kind === 'ok' ? 'text-fertile' : 'text-period'}`}>
            {msg.text}
          </p>
        )}
        {/* 清空邮箱是允许的，但代价要当面讲清楚，不能等他忘了口令才发现 */}
        {clearing && current && (
          <p className="text-sm text-period leading-relaxed">{t.settings.emailClearWarning}</p>
        )}
        {!recovery && !clearing && (
          <p className="text-xs text-period leading-relaxed">{t.settings.emailRecoveryOff}</p>
        )}
        <p className="text-xs text-muted leading-relaxed pt-1">{t.settings.emailWhy}</p>
        <p className="text-xs text-muted leading-relaxed">{t.settings.emailNeedsPassword}</p>
      </div>
    </Sheet>
  )
}

// ── 注销 ────────────────────────────────────────────────────────────────

function DangerSection() {
  const { t } = useI18n()
  const { snapshot } = useReady()
  const { signOut } = useStore()
  const [open, setOpen] = useState(false)
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isLast = snapshot.plan.memberCount <= 1
  const loginName = snapshot.user.loginName

  const submit = async () => {
    if (confirm.trim().toLowerCase() !== loginName) {
      return setError(t.danger.deleteMismatch)
    }
    setBusy(true)
    setError(null)
    try {
      await api.deleteAccount(confirm.trim())
      // 账号已经不存在了，本地 token 也就没意义了，直接回到登录页
      signOut()
    } catch (e) {
      setError(e instanceof Error ? e.message : t.common.error)
      setBusy(false)
    }
  }

  return (
    <div>
      <SectionTitle>{t.danger.title}</SectionTitle>
      <Card className="py-1">
        <Row
          label={t.danger.deleteAccount}
          hint={isLast ? t.danger.deleteHintLast : t.danger.deleteHintShared}
        >
          <Button variant="danger" className="text-sm" onClick={() => setOpen(true)}>
            {t.danger.deleteAccount}
          </Button>
        </Row>
      </Card>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={t.danger.deleteAccount}
        footer={
          <Button
            variant="danger"
            className="w-full"
            onClick={() => void submit()}
            disabled={busy}
          >
            {busy ? (
              <span className="inline-flex items-center gap-2">
                <Spinner /> {t.danger.deleteWorking}
              </span>
            ) : (
              t.danger.deleteSubmit
            )}
          </Button>
        }
      >
        <div className="space-y-3 pb-2">
          <p className="text-sm leading-relaxed">
            {isLast ? t.danger.deleteHintLast : t.danger.deleteHintShared}
          </p>
          <input
            type="text"
            autoCapitalize="none"
            autoCorrect="off"
            placeholder={t.danger.deleteConfirmLabel(loginName)}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full rounded-2xl bg-bg-soft px-4 py-3 text-[16px] outline-none border border-transparent focus:border-line"
          />
          {error && <p className="text-sm text-period">{error}</p>}
        </div>
      </Sheet>
    </div>
  )
}

function NumberStepper({
  value,
  min,
  max,
  disabled,
  onChange,
}: {
  value: number
  min: number
  max: number
  disabled?: boolean
  onChange: (v: number) => void
}) {
  const { t } = useI18n()
  const btn =
    'w-9 h-9 rounded-full bg-bg-soft grid place-items-center text-lg leading-none active:scale-90 transition disabled:opacity-40'
  return (
    <div className="flex items-center gap-3">
      <button
        className={btn}
        disabled={disabled || value <= min}
        onClick={() => onChange(value - 1)}
        aria-label={t.common.decrease}
      >
        −
      </button>
      <span className="w-10 text-center tabular-nums text-[15px]">{value}</span>
      <button
        className={btn}
        disabled={disabled || value >= max}
        onClick={() => onChange(value + 1)}
        aria-label={t.common.increase}
      >
        +
      </button>
    </div>
  )
}

function PasswordSheet({
  open,
  onClose,
  loginName,
}: {
  open: boolean
  onClose: () => void
  loginName: string
}) {
  const { t } = useI18n()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  const submit = async () => {
    if (next.length < 8) return setMsg({ kind: 'err', text: t.settings.passwordTooShort })
    if (next !== confirm) return setMsg({ kind: 'err', text: t.settings.passwordMismatch })
    setBusy(true)
    setMsg(null)
    try {
      await api.changePassword(loginName, current, next)
      setMsg({ kind: 'ok', text: t.settings.passwordChanged })
      setCurrent('')
      setNext('')
      setConfirm('')
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : t.common.error })
    } finally {
      setBusy(false)
    }
  }

  const input =
    'w-full rounded-2xl bg-bg-soft px-4 py-3 text-[15px] outline-none border border-transparent focus:border-line'

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t.settings.changePassword}
      footer={
        <Button className="w-full" onClick={submit} disabled={busy}>
          {busy ? (
            <span className="inline-flex items-center gap-2">
              <Spinner /> {t.settings.working}
            </span>
          ) : (
            t.settings.save
          )}
        </Button>
      }
    >
      <div className="space-y-3 pb-2">
        <input
          type="password"
          autoComplete="current-password"
          placeholder={t.settings.currentPassword}
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className={input}
        />
        <input
          type="password"
          autoComplete="new-password"
          placeholder={t.settings.newPassword}
          value={next}
          onChange={(e) => setNext(e.target.value)}
          className={input}
        />
        <input
          type="password"
          autoComplete="new-password"
          placeholder={t.settings.confirmPassword}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className={input}
        />
        {msg && (
          <p className={`text-sm ${msg.kind === 'ok' ? 'text-fertile' : 'text-period'}`}>
            {msg.text}
          </p>
        )}
        <p className="text-xs text-muted leading-relaxed pt-1">
          {t.settings.passwordNote(CLIENT_ITERATIONS.toLocaleString())}
        </p>
      </div>
    </Sheet>
  )
}
